import React, { useCallback, useLayoutEffect, useState } from 'react';
import { StyleSheet, View } from 'react-native';
import Clipboard from '@react-native-clipboard/clipboard';
import Button from 'design-system/Button';
import EmptyComponent from 'design-system/EmptyComponent';
import useDisableAndroidBackButton from 'hooks/useDisableAndroidBackButton';
import StatusBar from 'design-system/StatusBar';
import { ButtonGroup } from 'design-system/ButtonGroup';
import HelpScreenButton from 'features/help/components/HelpScreenButton';
import Text from 'design-system/Text';
import { AppView } from 'design-system/AppView';
import IconClose from 'design-system/icons/IconClose';
import useColors from 'hooks/useColors';
import Squircle from 'design-system/Squircle';
import { rem } from 'design-system/values';
import Spacer from 'design-system/Spacer';
import { CardView } from 'design-system/CardView';
import IconCopy from 'design-system/icons/IconCopy';
import TouchableOpacity from 'utils/packages/TouchableOpacity';
import { useTimeout } from 'hooks';
import { isAndroid } from '../../constants';
import ReferenceCopiedToast from './ReferenceCopiedToast';
export const ErrorScreen = React.memo(({ route, navigation }) => {
    const colors = useColors();
    const { title, message, reference, helpArticlesKey, onPrimaryPress, onSecondaryPress, onTertiaryPress, primaryButtonText, secondaryButtonText, tertiaryButtonText, } = route.params;
    useLayoutEffect(() => {
        navigation.setOptions({
            gestureEnabled: false,
            headerLeft: EmptyComponent,
            headerRight: helpArticlesKey
                ? () => <HelpScreenButton articlesKey={helpArticlesKey}/>
                : undefined,
        });
    }, [helpArticlesKey]);
    useDisableAndroidBackButton();
    const handlePrimaryPress = useCallback(() => {
        if (onPrimaryPress) {
            onPrimaryPress();
            return;
        }
        navigation.goBack();
    }, [onPrimaryPress]);
    const [copyToastVisible, setCopyToastVisible] = useState(false);
    useTimeout(() => setCopyToastVisible(false), copyToastVisible ? 4000 : undefined);
    const copyReference = useCallback(() => {
        if (!reference)
            return;
        Clipboard.setString(reference);
        if (!isAndroid) {
            setCopyToastVisible(true);
        }
    }, [reference]);
    return (<AppView avoidHeader withFrame paddingBottom>
        <StatusBar barStyle="dark-content" animated/>
        <View style={styles.content}>
          <Squircle color={colors.elements.statusNegativeLight} height={rem(88)} width={rem(88)}>
            <IconClose alt size={54} color={colors.elements.statusNegative}/>
          </Squircle>

          <Spacer.H24 />
          <Text Text-24 centered>
            {title}
          </Text>
          <Spacer.H8 />
          {typeof message === 'string' ? (<Text centered TextThin-16 Secondary style={styles.message}>
              {message}
            </Text>) : (message.map((msg, index) => (<Text key={index} centered TextThin-16 Secondary style={styles.message}>
                {msg}
              </Text>)))}

          <Spacer h={14}/>

          {reference ? (<View style={styles.referenceContainer}>
              <ReferenceCopiedToast isVisible={copyToastVisible} onClose={() => setCopyToastVisible(false)}/>
              <CardView withPadding noMargin>
                <Text TextThin-14 Secondary>
                  Reference number
                </Text>
                <TouchableOpacity hoverOnLight onPress={copyReference} style={styles.copyButton}>
                  <View style={styles.copyContainer}>
                    <Text Text-16 flex>
                      {reference}
                    </Text>
                    <IconCopy fill={colors.elements.secondary} width={20} height={20}/>
                  </View>
                </TouchableOpacity>
              </CardView>
            </View>) : null}
        </View>

        <ButtonGroup>
          {!!(onTertiaryPress && tertiaryButtonText) && (<Button {...(secondaryButtonText && onSecondaryPress
            ? { brandBorder: true }
            : { brandReversed: true })} brandBorder onPress={onTertiaryPress} title={tertiaryButtonText}/>)}
          {!!(secondaryButtonText && onSecondaryPress) && (<Button title={secondaryButtonText} onPress={onSecondaryPress} brandReversed/>)}
          <Button title={primaryButtonText || 'Okay'} onPress={handlePrimaryPress} brand/>
        </ButtonGroup>
      </AppView>);
});
const styles = StyleSheet.create({
    content: {
        flex: 1,
        alignItems: 'center',
        justifyContent: 'center',
        paddingHorizontal: rem(16),
    },
    message: {
        marginBottom: rem(10),
    },
    referenceContainer: {
        width: '100%',
    },
    copyButton: {
        width: '100%',
    },
    copyContainer: {
        flexDirection: 'row',
        gap: rem(16),
        width: '100%',
    },
});
